import { CheckCircle2, AlertCircle, Sparkles, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOwnerOnboarding } from '../../context/OwnerOnboardingContext';

interface OwnerOnboardingBannerProps {
  onNavigateToSettings: () => void;
}

export function OwnerOnboardingBanner({ onNavigateToSettings }: OwnerOnboardingBannerProps) {
  const { t } = useTranslation();
  const { onboardingState, isOnboardingComplete, getOnboardingProgress, isCheckingProfile } = useOwnerOnboarding();
  const { completed, total, percentage } = getOnboardingProgress();

  // Don't show banner while checking profile in database
  if (isCheckingProfile) {
    return null;
  }

  // Don't show banner if onboarding is complete
  if (isOnboardingComplete) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-200 rounded-lg p-6 mb-6">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-orange-600" />
          </div>
        </div>
        
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-xl font-semibold text-gray-900">{t('onboarding.banner.title')}</h3>
            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-sm rounded">
              {percentage}% {t('common.completed', 'Complete')}
            </span>
          </div>
          
          <p className="text-gray-700 mb-4">
            {t('onboarding.banner.description')}
          </p>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2 text-sm">
              <span className="text-gray-600">{t('onboarding.banner.setupProgress')}</span>
              <span className="text-gray-900 font-medium">{t('onboarding.banner.sectionsCompleted', { completed, total })}</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-500"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>

          {/* Checklist */}
          <div className="space-y-2 mb-4">
            <div className={`flex items-center gap-2 text-sm ${
              onboardingState.completedSections.basicInformation 
                ? 'text-green-700' 
                : 'text-gray-600'
            }`}>
              {onboardingState.completedSections.basicInformation ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-orange-500" />
              )}
              <span className="font-medium">{t('onboarding.banner.basicInformation')}</span>
              <span className="text-xs text-gray-500">{t('onboarding.banner.basicInfoHint')}</span>
              {onboardingState.completedSections.basicInformation && (
                <span className="ml-auto text-xs text-green-600">✓ {t('onboarding.banner.completed')}</span>
              )}
            </div>
          </div>

          <button
            onClick={onNavigateToSettings}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-lg hover:opacity-90 transition-opacity"
          >
            <span>{t('onboarding.banner.button')}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
